import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';
import MenuItem from '@mui/material/MenuItem';
import TextField from '@mui/material/TextField';
import ToggleButton from '@mui/material/ToggleButton';
import ToggleButtonGroup from '@mui/material/ToggleButtonGroup';
import Typography from '@mui/material/Typography';
import { DAY_NAMES, describeWindow, MAX_PERIODS, rateScheduleBody, weekSlots, type TouPeriod, type TouWindow } from '@pulsepoint/shared';
import { Pencil, Plus, Trash2, X } from 'lucide-react';
import { useState } from 'react';
import { ApiError } from '../../api/client';
import { useDeleteRateSchedule, useRateSchedules, useSaveRateSchedule, type RateSchedule } from '../../api/chargers';
import { usePermission } from '../../auth/permissions';
import { DataTable, type Column } from '../../components/DataTable';
import { useDialog, type DialogProps } from '../../components/dialogs/DialogProvider';
import { FormDialog } from '../../components/dialogs/FormDialog';
import { EmptyState } from '../../components/EmptyState';
import { RowMenu } from '../../components/RowMenu';
import { useToast } from '../../components/Toast';
import { useUnsavedChanges } from '../../components/unsaved/unsaved';
import { colours, radius } from '../../theme';

/** A colour per period, from the design guidelines: off-peak, peak, shoulder, super off-peak (by position). */
const SHADES = [colours.paleGrey, colours.graphite, colours.chargeMint, colours.signalTeal];
/** Hours and half hours to choose from (the schedule itself works to the quarter hour). */
const STEP_MINUTES = 30;
const TIMES = Array.from({ length: (24 * 60) / STEP_MINUTES + 1 }, (_, i) => {
  const m = i * STEP_MINUTES;
  return `${String(Math.floor(m / 60)).padStart(2, '0')}:${String(m % 60).padStart(2, '0')}`;
});
/** The choices, plus a time already saved off the half hour (so it still shows). */
const timesWith = (list: string[], value: string) => (list.includes(value) ? list : [...list, value].sort());

/**
 * Peak and off-peak hours (admins): named schedules sites choose, e.g. "Business hours" with
 * Peak on weekdays 9am to 6pm. Each charger's prices then have a rate for every period.
 */
export function RateSchedulesTab() {
  const canEdit = usePermission('tariffs.edit');
  const list = useRateSchedules(canEdit);
  const remove = useDeleteRateSchedule();
  const dialog = useDialog();
  const toast = useToast();

  const columns: Column<RateSchedule>[] = [
    { key: 'name', header: 'Schedule', sortValue: (r) => r.name.toLowerCase(), render: (r) => <Typography sx={{ fontWeight: 500 }}>{r.name}</Typography> },
    {
      key: 'periods',
      header: 'Periods',
      render: (r) => (
        <Box>
          {r.periods.map((p, i) => (
            <Typography key={p.name} variant="body2" color={i === 0 ? 'textSecondary' : undefined}>
              {p.name}: {i === 0 ? 'all other times' : p.windows.map(describeWindow).join('; ')}
            </Typography>
          ))}
        </Box>
      ),
    },
    { key: 'sites', header: 'Used by', hideBelow: 'sm', sortValue: (r) => r.siteCount, render: (r) => (r.siteCount ? <Typography variant="body2">{r.sites.join(', ')}</Typography> : <Typography color="textSecondary">No sites yet</Typography>) },
  ];

  return (
    <Box sx={{ display: 'grid', gap: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 2, flexWrap: 'wrap', alignItems: 'flex-start' }}>
        <Box sx={{ maxWidth: 640 }}>
          <Typography variant="h2">Peak and off-peak hours.</Typography>
          <Typography color="textSecondary" sx={{ mt: 0.5 }}>
            Choose one for a site (edit the site), then set a rate for each period on its chargers’ Pricing tab. Each slice of energy is charged at the rate for when it was delivered. Public holidays are priced like any other day.
          </Typography>
        </Box>
        <Button variant="contained" startIcon={<Plus size={20} strokeWidth={1.75} />} onClick={() => void dialog.open(ScheduleDialog, {})}>
          Add a schedule
        </Button>
      </Box>
      <DataTable
        label="Peak and off-peak hours"
        rows={list.data}
        loading={list.isPending}
        error={list.error}
        onRetry={() => void list.refetch()}
        columns={columns}
        rowKey={(r) => r.id}
        onRowClick={(r) => void dialog.open(ScheduleDialog, { schedule: r })}
        rowActions={(r) => (
          <RowMenu
            label={`Actions for ${r.name}`}
            items={[
              { label: 'Edit', icon: Pencil, onClick: () => void dialog.open(ScheduleDialog, { schedule: r }) },
              {
                label: 'Delete',
                icon: Trash2,
                danger: true,
                hidden: r.siteCount > 0,
                onClick: async () => {
                  if (!(await dialog.confirm({ title: `Delete ${r.name}?`, message: 'No site uses it.', confirmLabel: 'Delete', danger: true }))) return;
                  try {
                    await remove.mutateAsync(r.id);
                    toast(`${r.name} was deleted.`);
                  } catch (e) {
                    toast(e instanceof ApiError ? e.message : 'Something went wrong.', 'error');
                  }
                },
              },
            ]}
          />
        )}
        empty={<EmptyState title="No schedules yet">Without one, a site has one price all day.</EmptyState>}
      />
    </Box>
  );
}

const blankWindow = (): TouWindow => ({ days: [1, 2, 3, 4, 5], from: '09:00', to: '17:00' });

/** Add or edit a schedule: a name, then up to four periods; the first covers all other times. */
function ScheduleDialog({ schedule, close }: { schedule?: RateSchedule } & DialogProps) {
  const save = useSaveRateSchedule();
  const toast = useToast();
  const [name, setName] = useState(schedule?.name ?? '');
  const [periods, setPeriods] = useState<TouPeriod[]>(schedule?.periods ?? [{ name: 'Off-peak', windows: [] }, { name: 'Peak', windows: [blankWindow()] }]);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [error, setError] = useState('');
  const dirty = name !== (schedule?.name ?? '') || JSON.stringify(periods) !== JSON.stringify(schedule?.periods ?? null);

  const setPeriod = (i: number, p: Partial<TouPeriod>) => setPeriods((ps) => ps.map((x, j) => (j === i ? { ...x, ...p } : x)));
  const setWindow = (i: number, w: number, patch: Partial<TouWindow>) => setPeriod(i, { windows: periods[i]!.windows.map((x, k) => (k === w ? { ...x, ...patch } : x)) });

  const submit = async (): Promise<boolean> => {
    setError('');
    const parsed = rateScheduleBody.safeParse({ name, periods });
    if (!parsed.success) {
      setErrors(Object.fromEntries(parsed.error.issues.map((x) => [x.path.join('.'), x.message])));
      setError('Please check the highlighted fields');
      return false;
    }
    setErrors({});
    try {
      await save.mutateAsync({ id: schedule?.id, ...parsed.data });
      toast(`${parsed.data.name} was saved.${schedule?.siteCount ? ' Set new prices on its sites’ chargers so they follow the new hours.' : ''}`);
      close();
      return true;
    } catch (e) {
      if (e instanceof ApiError) setErrors(e.fields);
      setError(e instanceof ApiError ? e.message : 'Something went wrong.');
      return false;
    }
  };
  useUnsavedChanges({ dirty, save: submit, discard: () => undefined });

  const { slots } = weekSlots(periods);
  const err = (path: string) => errors[path];
  const windowErr = (i: number) => Object.entries(errors).find(([k]) => k.startsWith(`periods.${i}.windows`))?.[1];

  return (
    <FormDialog
      title={schedule ? `Edit ${schedule.name}` : 'Add peak and off-peak hours'}
      lead={schedule?.siteCount ? `Used by ${schedule.sites.join(', ')}. Their chargers keep the old hours until you set new prices.` : 'Times are on the site’s own clock, on the hour or half hour.'}
      submitLabel={save.isPending ? 'Saving…' : 'Save'}
      onSubmit={() => void submit()}
      onClose={() => close()}
      busy={save.isPending}
      error={error}
      maxWidth="md"
    >
      <Box sx={{ display: 'grid', gap: 3 }}>
        <TextField label="Name" value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g. Business hours" error={!!err('name')} helperText={err('name') ?? ' '} autoFocus sx={{ maxWidth: 360 }} />

        {periods.map((p, i) => (
          <Box key={i} sx={{ border: `1px solid ${colours.hairline}`, borderRadius: `${radius.panel}px`, p: 2, display: 'grid', gap: 1.5 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
              <Box aria-hidden sx={{ width: 14, height: 14, borderRadius: '3px', bgcolor: SHADES[i], border: `1px solid ${colours.hairline}`, flexShrink: 0 }} />
              <TextField size="small" label="Period" value={p.name} onChange={(e) => setPeriod(i, { name: e.target.value })} error={!!err(`periods.${i}.name`)} helperText={err(`periods.${i}.name`)} sx={{ width: 220 }} />
              <Typography color="textSecondary" sx={{ flex: 1 }}>{i === 0 ? 'All other times' : null}</Typography>
              {i > 0 && (
                <IconButton aria-label={`Remove ${p.name || 'this period'}`} onClick={() => setPeriods((ps) => ps.filter((_, j) => j !== i))}>
                  <Trash2 size={18} strokeWidth={1.75} />
                </IconButton>
              )}
            </Box>
            {i > 0 && (
              <>
                {p.windows.map((w, k) => (
                  <Box key={k} sx={{ display: 'flex', gap: 1.5, alignItems: 'center', flexWrap: 'wrap' }}>
                    <ToggleButtonGroup
                      size="small"
                      value={w.days}
                      onChange={(_e, days: number[]) => setWindow(i, k, { days: [...days].sort() })}
                      aria-label="Days"
                    >
                      {DAY_NAMES.map((d, di) => (
                        <ToggleButton key={d} value={di + 1} sx={{ px: 1.25 }}>{d}</ToggleButton>
                      ))}
                    </ToggleButtonGroup>
                    <TextField select size="small" label="From" value={w.from} onChange={(e) => setWindow(i, k, { from: e.target.value })} sx={{ width: 110 }}>
                      {timesWith(TIMES.slice(0, -1), w.from).map((t) => <MenuItem key={t} value={t}>{t}</MenuItem>)}
                    </TextField>
                    <TextField select size="small" label="To" value={w.to} onChange={(e) => setWindow(i, k, { to: e.target.value })} sx={{ width: 110 }}>
                      {timesWith(TIMES.slice(1), w.to).map((t) => <MenuItem key={t} value={t}>{t === '24:00' ? '24:00 (midnight)' : t}</MenuItem>)}
                    </TextField>
                    {w.to <= w.from && w.to !== '24:00' && <Typography variant="body2" color="textSecondary">runs past midnight</Typography>}
                    <IconButton aria-label="Remove these hours" onClick={() => setPeriod(i, { windows: p.windows.filter((_, j) => j !== k) })} disabled={p.windows.length === 1}>
                      <X size={18} strokeWidth={1.75} />
                    </IconButton>
                  </Box>
                ))}
                {windowErr(i) && <Typography color="error" variant="body2">{windowErr(i)}</Typography>}
                <Box>
                  <Button size="small" startIcon={<Plus size={16} strokeWidth={1.75} />} onClick={() => setPeriod(i, { windows: [...p.windows, { days: [6, 7], from: '09:00', to: '17:00' }] })}>
                    More hours
                  </Button>
                </Box>
              </>
            )}
          </Box>
        ))}

        {periods.length < MAX_PERIODS && (
          <Box>
            <Button startIcon={<Plus size={18} strokeWidth={1.75} />} onClick={() => setPeriods((ps) => [...ps, { name: ['Peak', 'Shoulder', 'Super off-peak', 'Period'][ps.length - 1] ?? 'Period', windows: [blankWindow()] }])}>
              Add a period
            </Button>
          </Box>
        )}

        <WeekChart slots={slots} periods={periods} />
      </Box>
    </FormDialog>
  );
}

/** The week at a glance: a bar per day, shaded by period, with a legend. */
function WeekChart({ slots, periods }: { slots: number[]; periods: TouPeriod[] }) {
  const perDay = slots.length / 7;
  return (
    <Box aria-label="The week at a glance" role="img" sx={{ display: 'grid', gap: 0.75 }}>
      {DAY_NAMES.map((d, di) => (
        <Box key={d} sx={{ display: 'grid', gridTemplateColumns: '40px 1fr', alignItems: 'center', gap: 1 }}>
          <Typography variant="body2" color="textSecondary">{d}</Typography>
          <Box sx={{ display: 'flex', height: 14, borderRadius: '3px', overflow: 'hidden', border: `1px solid ${colours.hairline}` }}>
            {slots.slice(di * perDay, (di + 1) * perDay).map((p, s) => (
              <Box key={s} sx={{ flex: 1, bgcolor: SHADES[p] }} />
            ))}
          </Box>
        </Box>
      ))}
      <Box sx={{ display: 'grid', gridTemplateColumns: '40px 1fr', gap: 1 }}>
        <span />
        <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
          {['00:00', '06:00', '12:00', '18:00', '24:00'].map((t) => (
            <Typography key={t} variant="caption" color="textSecondary">{t}</Typography>
          ))}
        </Box>
      </Box>
      <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', mt: 0.5 }}>
        {periods.map((p, i) => (
          <Box key={i} sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.75 }}>
            <Box aria-hidden sx={{ width: 12, height: 12, borderRadius: '3px', bgcolor: SHADES[i], border: `1px solid ${colours.hairline}` }} />
            <Typography variant="body2">{p.name || `Period ${i + 1}`}</Typography>
          </Box>
        ))}
      </Box>
    </Box>
  );
}
